import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import { formatTime12Hour } from "../../../utils/utils";
import {
  commonButtonProps,
  commonTextFieldProps,
  smallButtonProp,
  tableCellStyles,
  tableHeaderStyles,
} from "../../../utils/styles";
import { routeServiceApi } from "./apiService";

interface scheduleData {
  scheduleId: string;
  stopId: string;
  stopTime: string;
  createdBy: string;
}

const ScheduleView: React.FC<any> = ({ schedules, onSaveSchedule }) => {
  const localUser = sessionStorage.getItem("user");
  const userId = localUser ? JSON.parse(localUser).id : null;

  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(
    null
  );
  const [scheduleData, setScheduleData] = useState<scheduleData[]>([]);
  const [originalTimings, setOriginalTimings] = useState<any>({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentSchedule, setCurrentSchedule] = useState<any>(null);

  const handleEditSchedule = (schedule: any) => {
    setCurrentSchedule(schedule);
    setEditingScheduleId(schedule?.id);
    // Store original timings for this schedule
    setOriginalTimings({
      ...originalTimings,
      [schedule?.id]: schedule?.timings.map((timing: any) => ({
        id: timing.id,
        stopTime: timing.stopTime,
      })),
    });
    // Initialize scheduleData with current timings
    const initialData = schedule?.timings.map((timing: any) => ({
      scheduleId: schedule?.id,
      stopId: timing.stop.id,
      stopTime: timing.stopTime,
      createdBy: userId,
    }));
    setScheduleData(initialData);
    setIsDialogOpen(true);
  };

  const handleCancelEdit = () => {
    setEditingScheduleId(null);
    setScheduleData([]);
    setIsDialogOpen(false);
    setCurrentSchedule(null);
  };

  const handleSaveSchedule = async () => {
    try {
      const response = await routeServiceApi.updateSchedule(
        editingScheduleId!,
        scheduleData
      );
      if (response) {
        // Reset edit state
        setEditingScheduleId(null);
        setScheduleData([]);
        setIsDialogOpen(false);
        setCurrentSchedule(null);
        onSaveSchedule();
      } else {
        console.error("Failed to update schedule:", response);
      }
    } catch (error) {
      console.error("Error saving schedule:", error);
    }
  };

  const handleTimeChange = (
    scheduleId: string,
    stopId: string,
    newTime: string
  ) => {
    setScheduleData((prev) => {
      // Remove existing entry for this stop if it exists
      const filtered = prev.filter(
        (item) => !(item.scheduleId === scheduleId && item.stopId === stopId)
      );
      // Add new entry
      return [
        ...filtered,
        {
          scheduleId,
          stopId,
          stopTime: newTime,
          createdBy: userId,
        },
      ];
    });
  };

  // Get the first stop timing for display
  const getFirstStopTiming = (schedule: any) => {
    if (!schedule.timings || schedule.timings.length === 0) return null;

    // Sort by stop number and get the first one
    const sortedTimings = schedule.timings.sort(
      (a: any, b: any) => a.stop.stopNumber - b.stop.stopNumber
    );

    return sortedTimings[0];
  };

  return (
    <Box>
      {/* Simplified Schedule Cards */}
      {/* <Grid container spacing={2}> */}
      <Box
        sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 1 }}
      >
        {schedules?.map((schedule: any, scheduleIndex: any) => {
          const firstStop = getFirstStopTiming(schedule);
          return (
            <Box key={schedule.id} sx={{ width: { sm: "48%", xs: "100%" } }}>
              <Card sx={{ minWidth: "30%" }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Typography sx={{ fontSize: "0.8rem" }}>
                      {schedule?.name} : {scheduleIndex + 1} -{" "}
                      <b>{formatTime12Hour(firstStop?.stopTime)}</b>
                    </Typography>
                    <Button
                      variant="outlined"
                      color="primary"
                      {...smallButtonProp}
                      onClick={() => handleEditSchedule(schedule)}
                    >
                      Edit Schedule
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Box>

      {/* Edit Schedule Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCancelEdit}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Schedule -{" "}
          {currentSchedule?.id
            ? `Schedule ${
                schedules?.findIndex(
                  (s: any) => s?.id === currentSchedule?.id
                ) + 1
              }`
            : ""}
        </DialogTitle>
        <DialogContent>
          {currentSchedule &&
          currentSchedule?.timings &&
          currentSchedule?.timings?.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...tableHeaderStyles }}>
                      Stop Number
                    </TableCell>
                    <TableCell sx={{ ...tableHeaderStyles }}>
                      Stop Name
                    </TableCell>
                    <TableCell sx={{ ...tableHeaderStyles }}>
                      Stop Time
                    </TableCell>
                    <TableCell sx={{ ...tableHeaderStyles }}>
                      Contact Person
                    </TableCell>
                    <TableCell sx={{ ...tableHeaderStyles }}>
                      Contact Number
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentSchedule?.timings
                    .sort(
                      (a: any, b: any) => a.stop.stopNumber - b.stop.stopNumber
                    )
                    .map((timing: any, index: any) => (
                      <TableRow key={timing.id}>
                        <TableCell sx={tableCellStyles}>
                          {timing.stop.stopNumber}
                        </TableCell>
                        <TableCell sx={tableCellStyles}>
                          {timing.stop.stopName}
                        </TableCell>
                        <TableCell sx={tableCellStyles}>
                          <TextField
                            type="time"
                            value={
                              scheduleData.find(
                                (item) =>
                                  item?.stopId === timing?.stop?.id &&
                                  item?.scheduleId === currentSchedule?.id
                              )?.stopTime || timing?.stopTime
                            }
                            onChange={(e) =>
                              handleTimeChange(
                                currentSchedule?.id,
                                timing?.stop?.id,
                                e.target.value
                              )
                            }
                            {...commonTextFieldProps}
                          />
                        </TableCell>
                        <TableCell sx={tableCellStyles}>
                          {timing?.stop?.contactPersonName || "-"}
                        </TableCell>
                        <TableCell sx={tableCellStyles}>
                          {timing?.stop?.contactPersonNumber || "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography
              variant="body2"
              sx={{ textAlign: "center", color: "text.secondary", py: 2 }}
            >
              No timings found for this schedule
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelEdit}
            color="error"
            variant="outlined"
            {...commonButtonProps}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveSchedule}
            variant="contained"
            color="primary"
            {...commonButtonProps}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScheduleView;
